import _isEmpty from 'lodash/isEmpty';

/**
 * Processes finance offer data and updates state
 * @param {Object} financeOfferValues - Finance offer values
 * @param {Object} localFinanceObj - Local finance object to update
 * @param setFinanceOffersObj
 * @param vehicleCondition
 * @param financeOffersObj
 * @returns {Object} Updated finance object
 */
const processFinanceOfferData = (financeOfferValues, localFinanceObj, setFinanceOffersObj, vehicleCondition, financeOffersObj) => {
  if (_isEmpty(financeOfferValues)) {
    setFinanceOffersObj({
      ...localFinanceObj
    });
    return localFinanceObj;
  }
  const {
    payment,
    offeredBy,
    pricingDetails = {},
    incentiveIds,
    taxesFees,
    totalOfPayments = 0,
    specialApr,
    totalInterest = null
  } = financeOfferValues;
  const updatedFinanceObj = {
    ...localFinanceObj,
    lendingOptionSelected: !!financeOffersObj?.lendingOptionSelected,
    // Verify if this is needed or handle it otherway
    payment,
    lender: offeredBy,
    interestRate: financeOfferValues[vehicleCondition],
    manufacturerOffer: pricingDetails?.rebate,
    appliedIncentiveIds: incentiveIds ?? [],
    hasOffersOrIncentives: !_isEmpty(incentiveIds) || !!specialApr || !!pricingDetails?.rebate,
    pricingDetails,
    showSpecialAPR: specialApr,
    downPayment: financeOfferValues.downPayment,
    totalOfPayments,
    totalInterest,
    taxesFees: financeOfferValues.taxesAndFees ? {
      ...taxesFees,
      totalTaxesFees: financeOfferValues.taxesAndFees
    } : taxesFees,
    taxesAndFees: financeOfferValues?.taxesFees?.totalTaxesFees ?? 0
  };
  setFinanceOffersObj({
    ...updatedFinanceObj
  });
  return updatedFinanceObj;
};

/**
 * Processes lease offer data and updates state
 * @param {Object} leaseOfferValues - Lease offer values
 * @param {Object} localLeaseObj - Local lease object to update
 * @param setLeaseOffersObj
 * @param isLeaseEnabled
 * @param updateListingLeasePayments
 * @param updateMyWalletInteractions
 * @param listingId
 * @returns {Object} Updated lease object
 */
const processLeaseOfferData = (leaseOfferValues, localLeaseObj, setLeaseOffersObj, isLeaseEnabled, updateListingLeasePayments, updateMyWalletInteractions, listingId) => {
  if (!isLeaseEnabled) {
    const errorLeaseObj = {
      ...localLeaseObj,
      isPaymentError: true
    };
    setLeaseOffersObj({});
    return errorLeaseObj;
  }
  if (_isEmpty(leaseOfferValues)) {
    updateMyWalletInteractions({
      hasLeaseError: true
    });
    const errorLeaseObj = {
      ...localLeaseObj,
      isPaymentError: true
    };
    setLeaseOffersObj({});
    return errorLeaseObj;
  }
  const {
    pricingDetails = {},
    incentiveIds = [],
    specialApr
  } = leaseOfferValues;
  const updatedLeaseObj = {
    ...localLeaseObj,
    ...leaseOfferValues,
    appliedIncentiveIds: incentiveIds,
    taxesAndFees: leaseOfferValues?.taxesFees?.totalTaxesFees ?? 0,
    hasOffersOrIncentives: !_isEmpty(incentiveIds) || !!specialApr || !!pricingDetails?.rebate,
    isPaymentError: Object.keys(leaseOfferValues).length === 0 || leaseOfferValues.payment === undefined && leaseOfferValues.pricingDetails,
    pricingDetails,
    showSpecialAPR: specialApr
  };
  updateListingLeasePayments({
    [listingId]: {
      ...leaseOfferValues
    }
  });
  updateMyWalletInteractions({
    hasLeaseError: false
  });
  setLeaseOffersObj({
    ...updatedLeaseObj
  });
  return updatedLeaseObj;
};

/**
 * Processes cash offer data and updates state
 * @param {Object} cashOfferValues - Cash offer values
 * @param {Object} localCashObj - Local cash object to update
 * @param setCashOffersObj
 * @param isCashEnabled
 * @param updateListingCashPayments
 * @param updateMyWalletInteractions
 * @param listingId
 * @param localPrice
 * @param localTradeInValue
 * @param localAmountOwed
 * @returns {Object} Updated cash object
 */
const processCashOfferData = _ref => {
  let {
    cashOfferValues,
    localCashObj,
    setCashOffersObj,
    isCashEnabled,
    updateListingCashPayments,
    updateMyWalletInteractions,
    listingId,
    localPrice,
    localTradeInValue,
    localAmountOwed
  } = _ref;
  if (!isCashEnabled || _isEmpty(cashOfferValues)) {
    setCashOffersObj({
      ...localCashObj
    });
    return localCashObj;
  }
  const {
    incentiveIds = [],
    specialApr,
    pricingDetails: {
      salePrice,
      msrp,
      rebate
    } = {}
  } = cashOfferValues;
  const localCashPrice = salePrice || msrp || localPrice;
  const updatedCashObj = {
    ...cashOfferValues,
    ...localCashObj,
    appliedIncentiveIds: incentiveIds,
    taxesFees: cashOfferValues.taxesAndFees ? {
      ...cashOfferValues.taxesFees,
      totalTaxesFees: cashOfferValues.taxesAndFees
    } : cashOfferValues.taxesFees,
    taxesAndFees: cashOfferValues?.taxesFees?.totalTaxesFees ?? 0,
    payment: localCashPrice - localTradeInValue + localAmountOwed + (cashOfferValues?.taxesFees?.totalTaxesFees ?? 0),
    showSpecialAPR: specialApr,
    pricingDetails: cashOfferValues?.pricingDetails,
    hasOffersOrIncentives: !_isEmpty(incentiveIds) || specialApr || !!rebate
  };
  updateListingCashPayments({
    [listingId]: {
      ...cashOfferValues
    }
  });
  setCashOffersObj({
    ...updatedCashObj
  });
  return updatedCashObj;
};

/**
 * Applies payment calculation data when available
 * @param {Object} offerValues - offer values
 * @param localOfferObj
 * @param udsLocalStateValues
 * @param handlers
 * @param paymentsDuck
 * @param dispatch
 * @returns {Object} Updated offer objects
 */
const applyPaymentCalculationData = (offerValues, localOfferObj, udsLocalStateValues, handlers, paymentsDuck, dispatch) => {
  const {
    setFinanceOffersObj,
    setLeaseOffersObj,
    setCashOffersObj
  } = handlers;
  const {
    localFinanceObj,
    localLeaseObj,
    localCashObj
  } = localOfferObj;
  const updateListingLeasePayments = data => dispatch(paymentsDuck.creators.updateListingLeasePayments(data));
  const updateMyWalletInteractions = data => dispatch(paymentsDuck.creators.updateMyWalletInteractions(data));
  const updateListingCashPayments = data => dispatch(paymentsDuck.creators.updateListingCashPayments(data));
  const {
    financeOfferValues,
    leaseOfferValues,
    cashOfferValues
  } = offerValues || {};
  const {
    localPrice,
    localTradeInValue,
    localAmountOwed,
    isLeaseEnabled,
    isCashEnabled,
    financeOffersObj,
    listingId,
    vehicleCondition
  } = udsLocalStateValues || {};
  const updatedFinanceObj = processFinanceOfferData(financeOfferValues, localFinanceObj, setFinanceOffersObj, vehicleCondition, financeOffersObj);
  const updatedLeaseObj = processLeaseOfferData(leaseOfferValues, localLeaseObj, setLeaseOffersObj, isLeaseEnabled, updateListingLeasePayments, updateMyWalletInteractions, listingId);
  const updatedCashObj = processCashOfferData({
    cashOfferValues,
    localCashObj,
    setCashOffersObj,
    isCashEnabled,
    updateListingCashPayments,
    updateMyWalletInteractions,
    listingId,
    localPrice,
    localTradeInValue,
    localAmountOwed
  });
  return {
    updatedFinanceObj,
    updatedLeaseObj,
    updatedCashObj
  };
};
export { applyPaymentCalculationData as default };
